/**
 * @module PS
 * @description
 */
import Head from "next/head";

// Template
import Template from "@/templates/Default";

// Blocks
import BannerHeadline from "@/blocks/Banners/BannerHeadline";
import HeroAddressCheck from "@/blocks/Heroes/HeroAddressCheck";
import BannerList from "@/blocks/Banners/BannerList";
import SplitSectionCopy from "@/blocks/SplitSectionCopy";
import MonarchFallback from "@/components/blocks/MonarchFallback";

// Extras
import state from "@/js/config/state";

// Cohesion
import { MonarchSlot } from "@redventures/cohesion-utils-react";

const location = "PS";

/**
 * @function Page
 * @description
 */
const Page = () => (
  <>
    <Head>
      <title>Home | FrontierInternet</title>
    </Head>

    <Template
      title="Home"
      hero={
        <>
          {/* Home | Above Hero */}
          <MonarchSlot slotId="frinHomeAboveHero">
            <BannerHeadline
              title="Special Offer:"
              subtitle="Save $10 on your monthly bill when you establish and maintain Auto Pay."
            />
          </MonarchSlot>

          {/* Home | Hero */}
          <MonarchSlot
            slotId="frinHomeHero"
            fallback={<MonarchFallback height={["509px", "354px", "470px"]} />}
          >
            <HeroAddressCheck
              headline={
                state.is_desktop ? (
                  <>
                    Fiber&nbsp;Internet
                    <span className="u-color-blue">
                      At&nbsp;Light&nbsp;speed
                    </span>
                  </>
                ) : (
                  <>
                    Fiber
                    <br />
                    Internet&nbsp;At
                    <span className="u-color-blue">Light&nbsp;speed</span>
                  </>
                )
              }
              location={location}
              useSelectAreasLegal
            />
          </MonarchSlot>

          {/* Home | Below Hero  */}
          <MonarchSlot
            slotId="frinHomeBelowHero"
            fallback={<MonarchFallback height={["112px", "71px", "85px"]} />}
          >
            <BannerList
              theme="blue"
              items={[
                {
                  copy: "For smart homes with dozens of devices",
                  icon: "checkmark1",
                },
                {
                  copy: (
                    <>
                      $10/mo. off YouTube TV for one year
                      <span data-legal="youtubetv" />
                    </>
                  ),
                  icon: "checkmark1",
                },
                {
                  copy: (
                    <>
                      Amazon eero Pro 6 Wi-Fi router
                      <span data-legal="eero" />
                    </>
                  ),
                  icon: "checkmark1",
                },
              ]}
            />
          </MonarchSlot>
        </>
      }
      primary={
        <>
          {/* Home | Section 1 */}
          <MonarchSlot
            slotId="frinHomeSection1"
            fallback={<MonarchFallback height={["706px", "418px", "498px"]} />}
          >
            <SplitSectionCopy
              title="Why choose Frontier?"
              subtitle="We’re here to make internet easy"
              location={location}
              content={[
                {
                  headline: null,
                  copy: (
                    <>
                      Life is hard enough without complicated home services.
                      That's why we offer Frontier Internet and Frontier Home
                      Phone
                      <span key="span-3" data-legal="frontier-voice-line" /> for
                      your communication needs. All at an affordable price.
                    </>
                  ),
                },
              ]}
              image={{
                url: "/frin/woman-on-laptop.jpg",
                blurUrl: "/frin/woman-on-laptop.jpg",
                alt: "Woman smiling at laptop",
              }}
              theme="dark"
            />
          </MonarchSlot>

          {/* Home | Section 2 */}
          <MonarchSlot
            slotId="frinHomeSection2"
            fallback={<MonarchFallback height={["630px", "400px", "471px"]} />}
          >
            <SplitSectionCopy
              title="Connect at the price you want"
              subtitle="With a variety of options, we have plans to meet every budget."
              location={location}
              content={[
                {
                  headline: null,
                  copy: "Purchase Frontier Internet without breaking a sweat.",
                },
              ]}
              image={{
                url: "/sections/split-section/man-at-table-using-laptop.jpg",
                blurUrl:
                  "/sections/split-section/man-at-table-using-laptop.jpg",
                alt: "Man sitting at table using laptop",
              }}
              reverseLayout
            />
          </MonarchSlot>

          {/* Home | Section 3 */}
          <MonarchSlot slotId="frinHomeSection3" />

          {/* Home | Section 4 */}
          <MonarchSlot slotId="frinHomeSection4" />

          {/* Home | Section 5 */}
          <MonarchSlot slotId="frinHomeSection5" />

          {/* Home | Section 6 */}
          <MonarchSlot slotId="frinHomeSection6" />

          {/* Home | Section 7 */}
          <MonarchSlot slotId="frinHomeSection7" />
        </>
      }
    />
  </>
);

export default Page;
